<template>
  <div class="time-table">
    <time-table-header
      :flashMsgFlg="flashMsgFlg"
      :flashMsg="flashMsg"
      :weekTableDisplayFlg="weekTableDisplayFlg"
      @request-clear="requestClear"
    />
    <time-table-body
      v-if="!weekTableDisplayFlg"
      :initialReservationProp="initialReservation"
      @update-initial-reservation="updateInitialReservation"
      @open-week-table="openWeekTable"
    />
    <time-table-body-week
      v-if="weekTableDisplayFlg"
      @week-update-initial-reservation="weekUpdateInitialReservation"
      @open-one-day-table="$emit('open-one-day-table')"
    />
    <reservation-popup
      v-if="popupFlg"
      :reservationRequestId="reservationRequestId"
      :initialReservationProp="initialReservation"
      @close="closeReservation"
    />
    <announce-popup
      v-if="announceState.isShow"
      :title="announceState.title"
      :buttons="announceState.buttons"
      :type="announceState.type"
      @close="hideAnnounce"
      @decision="goToPaymentShowPage"
      @cancel="hideAnnounce"
      >{{ announceState.message }}</announce-popup
    >
  </div>
</template>

<script>
import AnnouncePopup from '@/components/popups/AnnouncePopup'
import ReservationPopup from '@/components/popups/ReservationPopup'
import TimeTableBody from '@/components/parts/organisms/TimeTableBody'
import TimeTableBodyWeek from '@/components/parts/organisms/TimeTableBodyWeek'
import TimeTableHeader from '@/components/parts/organisms/TimeTableHeader'
import moment from 'moment'

export default {
  name: 'TimeTable',
  components: {
    AnnouncePopup,
    ReservationPopup,
    TimeTableBody,
    TimeTableBodyWeek,
    TimeTableHeader
  },
  props: {
    weekTableDisplayFlg: { type: Boolean, default: false }
  },
  data() {
    return {
      initialReservation: null,
      reservationRequestId: 0,
      flashMsgFlg: false,
      flashMsg: '',
      timerId: null,
      announceState: {
        isShow: false,
        title: '',
        buttons: [],
        type: '',
        message: ''
      }
    }
  },
  computed: {
    popupFlg() {
      return this.$store.getters['timeTable/get'].popupFlg
    },
    timeTableDate() {
      return this.$store.getters['timeTable/get'].date
    }
  },
  watch: {
    timeTableDate: {
      async handler() {
        await this.getPastReservationsByDate()
      },
      immediate: true
    },
    weekTableDisplayFlg: {
      async handler(newVal) {
        if (newVal) {
          await this.getPastReservationsByDate()
        }
      }
    }
  },
  created() {
    const { initialReservation, reservationRequestId } = this.$store.getters[
      'timeTable/get'
    ]
    this.initialReservation = initialReservation
    this.reservationRequestId = reservationRequestId
  },
  beforeDestroy() {
    clearTimeout(this.timerId)
  },
  destroyed() {
    if (!this.$route.params.owner) {
      this.$store.dispatch('timeTable/deleteInitials')
    }
  },
  methods: {
    updateInitialReservation(initialReservation) {
      this.initialReservation = initialReservation
    },
    openWeekTable() {
      this.$store.dispatch('timeTable/deleteInitials')
      this.requestClear()
      this.$emit('open-week-table')
    },
    weekUpdateInitialReservation(initialReservation) {
      this.initialReservation = initialReservation
      this.$emit('open-one-day-table')
    },
    buildFlashMessage(result) {
      const baseByAction = {
        create: '予約を登録',
        update: '予約を編集',
        delete: '予約を削除',
        cancel: '予約をキャンセル',
        'create-repeat': '複数予約を登録',
        'delete-repeat': '複数予約を削除'
      }
      const base = baseByAction[result.action]
      if (!base) return ''
      let message =
        base + (result.sendMailFlg ? 'して、メールを送信しました' : 'しました')
      if (result.action === 'update' && result.isNewOwnerDeleted) {
        message += '。新規飼主を削除しました'
      }
      return message
    },
    closeReservation(result) {
      const message = this.buildFlashMessage(result)
      if (message) {
        this.flashMsg = message
        this.flashMsgFlg = true
        this.timerId = setTimeout(() => {
          this.flashMsgFlg = false
          this.flashMsg = ''
        }, 3000)
      }
      this.requestClear()
      this.$store.dispatch('timeTable/deletePopup')
      if (
        this.$route.params.fromPage === 'payment-show' &&
        (result.action === 'create' || result.action === 'create-repeat') &&
        !result.pushToAnotherPageFlg
      ) {
        this.showAnnounce({
          title: '確認',
          buttons: ['戻らない', '戻る'],
          type: 'alert',
          message: '会計画面に戻りますか？'
        })
      }
    },
    requestClear() {
      this.initialReservation = null
      this.reservationRequestId = 0
    },
    goToPaymentShowPage() {
      const {
        ownerId,
        patientId,
        medicalPaymentOriginalId
      } = this.$route.params.paymentShowPageIdInfo
      this.$router.push(
        `/main/karte/owners/${ownerId}/patients/${patientId}/payments/${medicalPaymentOriginalId}/show`
      )
    },
    async getPastReservationsByDate() {
      const requestDates = this.generateRequestDates()
      if (requestDates.length === 0) return
      const isSuccess = await this.$store.dispatch(
        'reservations/getPastData',
        requestDates
      )
      if (isSuccess) {
        this.$store.dispatch('reservations/addDates', requestDates)
      } else {
        this.showAnnounce({})
      }
    },
    generateRequestDates() {
      const candidateDates = this.weekTableDisplayFlg
        ? Array.from({ length: 7 }, (_, i) =>
            moment(this.timeTableDate)
              .add(i, 'days')
              .format('YYYYMMDD')
          )
        : [this.timeTableDate]
      const obtainedDates = this.$store.getters['reservations/getObtainedDates']
      const sevenDaysAgo = moment()
        .subtract(7, 'days')
        .format('YYYYMMDD')
      return candidateDates.filter(
        date => !obtainedDates.includes(date) && date < sevenDaysAgo
      )
    },
    showAnnounce({
      title = '失敗',
      message = '過去の予約の取得に失敗しました',
      buttons = ['閉じる'],
      type = 'failure'
    }) {
      this.announceState.isShow = true
      this.announceState.title = title
      this.announceState.message = message
      this.announceState.buttons = buttons
      this.announceState.type = type
    },
    hideAnnounce() {
      this.announceState.isShow = false
    }
  }
}
</script>

<style lang="scss" scoped>
.time-table {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-width: 0;
}
</style>
